import React, { ReactNode } from 'react';
import { createStyles } from '../../utils/css';

interface IProps {
  /** Target ratio of width / height from which height is determined */
  aspectRatio: number;
  /** Optional minimum height regardless of aspect ratio */
  minHeight?: string;
  minWidth?: string;
  /** Optional maximum height regardless of aspect ratio */
  maxHeight?: string;
  maxWidth?: string;
  children?: ReactNode;
}

export const isAspectRatioValid = (aspectRatio: number) =>
  Number.isFinite(aspectRatio) && aspectRatio > 0;

/**
 * Takes auto width (usually 100% but depends on flexbox, etc.) and
 * adjusts height of the element to a specific aspect ratio within
 * optional `minHeight` and `maxHeight` constraints. `minHeight`
 * and `maxHeight` take precedence over the aspect ratio so if you
 * pass one of them, it's likely the apect ratio won't be preserved.
 */
export default function FixedAspectRatio({
  aspectRatio,
  children,
  minHeight,
  maxHeight,
  minWidth,
  maxWidth,
}: IProps) {
  const height = isAspectRatioValid(aspectRatio) ? 100 / aspectRatio : 0;

  return (
    <Styles>{(css) =>
      <div className={css('container', { minHeight, maxHeight, minWidth, maxWidth })}>
        <div style={{ paddingTop: `${height}%` }} />

        <div className={css('content')}>
          {children}
        </div>
      </div>
    }</Styles>
  );
}

const Styles = createStyles({
  container: ({ minHeight, maxHeight, minWidth, maxWidth }) => ({
    maxHeight,
    minHeight,
    minWidth,
    maxWidth,
    position: 'relative',
    overflow: 'hidden',
    width:    '100%',
  }),
  content: {
    position: 'absolute',
    top:      '0',
    left:     '0',
    right:    '0',
    bottom:   '0',
    display:  'flex',
  },
});
